import React, { useState } from "react";
import { Alert } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { KnowledgeItem } from "@jugl-web/rest-api/ai-workflows/types";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { AgentKnowledgeContentContainer } from "../AgentKnowledgeContentContainer";
import { ReactComponent as QuestionIcon } from "./assets/question.svg";
import { ReactComponent as ChatQuestionIcon } from "./assets/chat-question.svg";
import { AgentKnowledgeItemContainer } from "../AgentKnowledgeItemContainer";
import { useAgentKnowledge } from "../../../../../../hooks/useAgentKnowledge";
import { QuestionsKnowledgeForm } from "./components/QuestionsKnowledgeForm";

export const AgentKnowledgeQuestionsContent: React.FC<{
  entityId: string;
}> = ({ entityId }) => {
  const { t } = useTranslations();
  const { quesKnowledge, handleDeleteKnowledge, isAnyKnowledgeFetching } =
    useAgentKnowledge(entityId);
  const [isFormOpen, setIsFormOpen] = useState(!quesKnowledge?.length);
  const [editedKnowledge, setEditedKnowledge] =
    useState<KnowledgeItem<"ques">>();
  const { isSubscriptionCanceled } = useSubscriptionV2();

  const deleteConfirmationAlertState = useConfirmationDialogState();

  const hasItems = !!quesKnowledge?.length;

  if (isFormOpen || !hasItems || editedKnowledge) {
    return (
      <QuestionsKnowledgeForm
        entityId={entityId}
        isFirstItem={!hasItems}
        isDisabled={isAnyKnowledgeFetching || isSubscriptionCanceled}
        onRequestClose={() => {
          setIsFormOpen(false);
          setEditedKnowledge(undefined);
        }}
        editedKnowledge={editedKnowledge}
      />
    );
  }

  return (
    <>
      <AgentKnowledgeContentContainer
        icon={<QuestionIcon />}
        title={t({
          id: "cx-ai-agents-page.questions-and-answers",
          defaultMessage: "Q&A",
        })}
        description={t({
          id: "cx-ai-agents-page.add-specific-answers-to-questions",
          defaultMessage:
            "Add specific answers to questions, so Agent will always answer correct",
        })}
        onAddItem={() => setIsFormOpen(true)}
      >
        <div className="flex flex-col gap-4">
          <span className="font-secondary font-medium text-black">
            {t({
              id: "cx-ai-agents-page.added-qas",
              defaultMessage: "Added Q&A’s",
            })}
          </span>
          <div className="flex flex-col gap-6">
            {quesKnowledge.map((item) => {
              const firstElement = item.data.source[0];

              if (!firstElement) {
                return null;
              }

              return (
                <AgentKnowledgeItemContainer
                  key={item.data.id}
                  title={firstElement.question}
                  icon={<ChatQuestionIcon />}
                  onDelete={
                    !isSubscriptionCanceled
                      ? () =>
                          deleteConfirmationAlertState.open({
                            onConfirm: () =>
                              handleDeleteKnowledge(item.data.id, "ques"),
                          })
                      : undefined
                  }
                  onClick={() => setEditedKnowledge(item)}
                >
                  {firstElement.answer}
                </AgentKnowledgeItemContainer>
              );
            })}
          </div>
        </div>
      </AgentKnowledgeContentContainer>
      <Alert
        isOpen={deleteConfirmationAlertState.isOpen}
        onRequestClose={deleteConfirmationAlertState.close}
        title={t({
          id: "cx-ai-agents-page.delete-item",
          defaultMessage: "Delete Item",
        })}
        content={t({
          id: "cx-ai-agents-page.delete-qas-knowledge-description-no-longer-trained",
          defaultMessage:
            "If you will delete selected Q&A, AI Agent will no longer be trained on its info",
        })}
        buttons={[
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            role: "close",
          },
          {
            text: t({ id: "common.delete", defaultMessage: "Delete" }),
            color: "tertiary",
            onClick: () => {
              deleteConfirmationAlertState.confirm({
                closeOnceConfirmed: true,
              });
            },
          },
        ]}
      />
    </>
  );
};
